import { AbstractControl, ValidationErrors, ValidatorFn } from '@angular/forms';

export const passwordMatchValidator: ValidatorFn = (
  control: AbstractControl
): ValidationErrors | null => {
  const newPassword = control.get('inputNewPassword');
  const confirmPassword = control.get('inputConfirmPassword');

  // Only compare once BOTH fields hold a value. A half-typed pair is not a
  // mismatch — the user simply has not reached the other field yet — and
  // flagging it reddens the box on the first keystroke.
  if (!newPassword?.value || !confirmPassword?.value) {
    return null;
  }

  return newPassword.value !== confirmPassword.value
    ? { passwordMismatch: true }
    : null;
};

export const passwordFieldValidator: ValidatorFn = (
  control: AbstractControl
): ValidationErrors | null => {
  const newPassword = control.get('inputNewPassword');
  const confirmPassword = control.get('inputConfirmPassword');
  const currentPassword = control.get('inputCurrentPassword');

  const newPasswordFilled = newPassword?.value;
  const confirmPasswordFilled = confirmPassword?.value;
  const currentPasswordFilled = currentPassword?.value;

  // If at least one field is filled, but at least one is still empty, throw an error
  if (
    (newPasswordFilled || confirmPasswordFilled || currentPasswordFilled) &&
    (!newPasswordFilled || !confirmPasswordFilled || !currentPasswordFilled)
  ) {
    return { passwordFieldRequired: true };
  }

  return null;
};

export const newPasswordRule: ValidatorFn = (
  control: AbstractControl
): ValidationErrors | null => {
  const password = control.value;

  // Emptiness guard runs FIRST: `reset()` with no argument seats null, and
  // dereferencing it below would throw inside updateValueAndValidity and take
  // the whole form's validation down. Required-ness is enforced separately.
  if (!password) {
    return null;
  }

  const errors: ValidationErrors = {};
  const hasLength = password.length >= 8 && password.length <= 12;
  const hasUppercase = /[A-Z]/.test(password);
  const hasLowercase = /[a-z]/.test(password);
  const hasNumber = /\d/.test(password);
  const hasSpecialChar = /[^A-Za-z0-9]/.test(password);

  if (!hasLength) {
    errors['length'] = true;
  }
  if (!hasUppercase || !hasLowercase) {
    errors['case'] = true;
  }
  if (!hasNumber) {
    errors['number'] = true;
  }
  if (!hasSpecialChar) {
    errors['special'] = true;
  }
  return Object.keys(errors).length > 0 ? errors : null;
};

export interface PasswordRule {
  text: string;
  rule: string;
}

export const passwordRules: PasswordRule[] = [
  { text: 'At least 8-12 characters', rule: 'length' },
  { text: 'Includes uppercase & lowercase letters', rule: 'case' },
  { text: 'Contains at least one number (0-9)', rule: 'number' },
  {
    text: 'Contains at least one special character (@, #, $, etc.)',
    rule: 'special',
  },
];

export const passwordNotSameValidator: ValidatorFn = (
  control: AbstractControl
): ValidationErrors | null => {
  const newPassword = control.get('inputNewPassword');
  const currentPassword = control.get('inputCurrentPassword');

  if (
    newPassword &&
    currentPassword &&
    newPassword.value &&
    currentPassword.value &&
    newPassword.value === currentPassword.value
  ) {
    return { sameAsCurrent: true };
  }

  return null;
};

/**
 * Parse a date string in DD/MM/YYYY, DD-MM-YYYY, or YYYY-MM-DD format to a Date object
 *
 * @param dateString - Date string in DD/MM/YYYY, DD-MM-YYYY, or YYYY-MM-DD format
 * @returns Date object if valid, null otherwise
 */
export function parseDDMMYYYY(dateString: string): Date | null {
  if (!dateString) return null;

  // Try DD-MM-YYYY or DD/MM/YYYY format first
  const ddmmyyyyRegex = /^(\d{2})[-\/]?(\d{2})[-\/]?(\d{4})$/;
  const ddmmyyyyMatch = dateString.match(ddmmyyyyRegex);

  if (ddmmyyyyMatch) {
    const day = Number(ddmmyyyyMatch[1]);
    const month = Number(ddmmyyyyMatch[2]);
    const year = Number(ddmmyyyyMatch[3]);

    // Validate date parts
    if (day > 0 && day <= 31 && month > 0 && month <= 12) {
      const formattedDate = new Date(year, month - 1, day);

      if (!isNaN(formattedDate.getTime())) {
        return formattedDate;
      }
    }
  }

  // Try YYYY-MM-DD format (ISO format)
  const yyyymmddRegex = /^(\d{4})[-\/](\d{2})[-\/](\d{2})$/;
  const yyyymmddMatch = dateString.match(yyyymmddRegex);

  if (yyyymmddMatch) {
    const year = Number(yyyymmddMatch[1]);
    const month = Number(yyyymmddMatch[2]);
    const day = Number(yyyymmddMatch[3]);

    // Validate date parts
    if (day > 0 && day <= 31 && month > 0 && month <= 12) {
      const formattedDate = new Date(year, month - 1, day);

      if (!isNaN(formattedDate.getTime())) {
        return formattedDate;
      }
    }
  }

  return null;
}

/**
 * Parse a date string flexibly, handling DD/MM/YYYY, YYYY-MM-DD, and ISO 8601 formats.
 * Backend may send dates in different formats depending on the field:
 * - FilingInfo dates use ISO 8601 (e.g., "2025-01-31T16:00:00.000Z")
 * - Signing dates use DD/MM/YYYY (e.g., "31/01/2025")
 *
 * IMPORTANT: Do NOT use native new Date() for slash-separated dates because
 * JavaScript interprets "DD/MM/YYYY" as "MM/DD/YYYY" (US format), which
 * causes "31/01/2025" to become Invalid Date while "01/31/2025" works incorrectly.
 *
 * @param dateString - Date string in any supported format
 * @returns Date object if valid, null otherwise
 */
export function parseFlexibleDate(dateString: string | Date): Date | null {
  if (!dateString) return null;

  // If it's already a Date object (e.g., from PrimeNG DatePicker), return as-is
  if (dateString instanceof Date) {
    return isNaN(dateString.getTime()) ? null : dateString;
  }

  const trimmed = String(dateString).trim();
  if (!trimmed) return null;

  // 1. Try splitting by "/" or "-" to handle DD/MM/YYYY and YYYY-MM-DD
  const parts = trimmed.split(/[\/\-]/);

  if (parts.length === 3) {
    let day: number, month: number, year: number;

    if (parts[0].length === 4) {
      // YYYY-MM-DD format
      year = Number(parts[0]);
      month = Number(parts[1]);
      day = Number(parts[2]);
    } else {
      // DD/MM/YYYY format
      day = Number(parts[0]);
      month = Number(parts[1]);
      year = Number(parts[2]);
    }

    if (day >= 1 && day <= 31 && month >= 1 && month <= 12 && year >= 1900) {
      const date = new Date(year, month - 1, day);
      // Verify the date didn't overflow (e.g., Feb 30 → Mar 2)
      if (date.getFullYear() === year && date.getMonth() === month - 1 && date.getDate() === day) {
        return date;
      }
    }
  }

  // 2. Full ISO 8601 with time (e.g., "2025-01-31T16:00:00.000Z")
  if (trimmed.includes('T')) {
    const date = new Date(trimmed);
    if (!isNaN(date.getTime())) return date;
  }

  return null;
}

/**
 * Handle date input change event for PrimeNG DatePicker
 * Parses DD/MM/YYYY format and updates the form control
 *
 * @param event - Input event from the date field
 * @param formGroup - The FormGroup containing the date control
 * @returns true if date was parsed and set, false otherwise
 */
export function handleDateInputChange(
  event: InputEvent,
  formGroup: AbstractControl
): boolean {
  const input = event.target as HTMLInputElement;
  const id = input.id;
  const dateString = input.value;
  const control = formGroup.get(id);

  if (!dateString) {
    clearStrandedDateText(control);
    return false;
  }

  const parsedDate = parseDDMMYYYY(dateString);

  if (parsedDate) {
    control?.setValue(parsedDate);
    return true;
  }

  clearStrandedDateText(control);
  return false;
}

/**
 * Null a date control whose model holds raw TEXT rather than a `Date`, without
 * touching what the input shows.
 *
 * Only reachable on a picker with `[keepInvalid]="true"` (today: the current
 * financial year END date on role0/role1). There PrimeNG's `onUserInput` puts
 * the typed string itself into the model while it cannot parse — `"2016-1"`
 * half-way through a date, or `''` once the field is cleared. Left alone, that
 * string would be auto-saved into state, and on the next load
 * `coerceSchemaDateStrings` would happily read `"2016-1"` as 1 January 2016.
 *
 * `emitModelToViewChange: false` is the point: it skips `writeValue`, so the
 * digits the user is still typing stay on screen (see
 * `datepicker-null-model-repaint.spec.ts` for why a view write would blank
 * them). On a picker without `keepInvalid` PrimeNG has already nulled the
 * model, so this is a no-op there.
 */
function clearStrandedDateText(control: AbstractControl | null): void {
  if (control && typeof control.value === 'string') {
    control.setValue(null, { emitModelToViewChange: false });
  }
}

/**
 * Handle a paste into a PrimeNG DatePicker's input.
 *
 * PrimeNG's `onUserInput` ignores any input event that no `keydown` came
 * before, so a date pasted from the right-click menu (or dropped in with the
 * mouse) never reaches the model: the text shows until the field loses focus
 * and then disappears. Ctrl+V happens to work only because the Ctrl key fires
 * a `keydown`.
 *
 * This takes the clipboard text as the WHOLE date — a date field has no use
 * for a partial paste — parses it with the same `parseDDMMYYYY` the typed path
 * uses, and writes it to the control. It then cancels the browser's own paste,
 * which would otherwise insert the text a second time next to the value we
 * just wrote. Text that is not a date is left to the browser's default paste.
 *
 * Bind on the `<p-datepicker>` host: `(paste)="onDatePaste($event)"`. The
 * input's `id` must be the control name, as it already is via `[inputId]`.
 *
 * @returns true if the pasted text was a date and was applied
 */
export function handleDatePaste(
  event: ClipboardEvent,
  formGroup: AbstractControl
): boolean {
  const input = event.target as HTMLInputElement | null;
  const control = input?.id ? formGroup.get(input.id) : null;
  if (!control || control.disabled) return false;

  // Spreadsheet cells copy with a trailing newline, so trim before parsing.
  const pastedText = event.clipboardData?.getData('text')?.trim();
  if (!pastedText) return false;

  const parsedDate = parseDDMMYYYY(pastedText);
  if (!parsedDate) return false;

  event.preventDefault();
  control.setValue(parsedDate);
  control.markAsDirty();
  control.markAsTouched();
  return true;
}

/**
 * Get max length for a form field based on schema configuration
 * Looks for maxLength validator in schema control definition
 *
 * @param fieldName - The qname of the field
 * @param schema - The form schema containing controls definitions
 * @returns The maxLength value if defined in schema, null otherwise
 */
export function getFieldMaxLength(
  fieldName: string,
  schema: { controls: Array<{ qname: string; maxLength?: number; ui: { control: string; maxLength?: number }; validators?: Array<{ name: string; value?: number }> }> } | null | undefined
): number | null {
  if (!schema?.controls) return null;

  const schemaControl = schema.controls.find(control => control.qname === fieldName);

  // Check if this is a text control from schema
  if (schemaControl?.ui.control === 'text') {
    // First check for direct maxLength property on control
    if (schemaControl.maxLength !== undefined) {
      return schemaControl.maxLength;
    }

    // Check for maxLength inside ui object
    if (schemaControl.ui.maxLength !== undefined) {
      return schemaControl.ui.maxLength;
    }

    // Fallback to validators array format
    const maxLengthValidator = schemaControl?.validators?.find(validator => validator.name === 'maxLength');
    if (maxLengthValidator && maxLengthValidator.value !== undefined) {
      return maxLengthValidator.value;
    }
  }

  return null;
}

/**
 * Does this value break the schema's declared `maxLength` for the field?
 *
 * The companion to {@link getFieldMaxLength}, so the mounted editor's
 * `Validators.maxLength` and a pure validator's headless check read ONE
 * definition of the cap. Roles 4 and 5 both call it; their sheet-selector
 * verdict and the listing verdict cannot disagree about an over-length value.
 *
 * Returns false when the schema declares no cap for the field, or when the
 * field is not a text control — `getFieldMaxLength` already scopes that.
 * An absent/blank value is never over-length; emptiness is `required`'s job.
 */
export function exceedsSchemaMaxLength(
  value: unknown,
  fieldName: string,
  schema: Parameters<typeof getFieldMaxLength>[1]
): boolean {
  const cap = getFieldMaxLength(fieldName, schema);
  if (cap === null || value === null || value === undefined) {
    return false;
  }
  return String(value).length > cap;
}
